import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SectionContent, Video, SectionContentSession } from '@stuplay';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { TimerService } from '../../../utils/components/timer/timer.service';
import { StorageService } from 'src/app/utils/services/storage.service';
import { VideoPlayer } from './video-player/video-player.interface';
import { createVideoPlayer } from './video-player/video-player.factory';

const RESUME_THRESHOLD_SECONDS = 30;
const SAVE_INTERVAL_MS = 5000;

@Component({
    selector: 'msc-video',
    templateUrl: 'video.component.html'
})
export class VideoComponent implements OnInit, OnDestroy {
    @ViewChild('playerContainer', { static: false }) playerContainerRef!: ElementRef<HTMLDivElement>;

    video!: Video;
    videoSubscription!: Subscription;
    sectionContent!: SectionContent;
    sectionContentSession!: SectionContentSession;

    showResumePrompt = false;
    isMaximized = false;
    private player: VideoPlayer | undefined;
    private saveIntervalId: any;
    private companyId!: number;

    constructor(
        private route: ActivatedRoute,
        public timerService: TimerService,
        private storageService: StorageService,
    ) {
        this.videoSubscription = this.route.paramMap.subscribe(() => {
            this.video = this.route.snapshot.data.video.video;
            this.sectionContent = this.route.snapshot.data.video;
            this.sectionContentSession = this.route.snapshot.data.sectionContentSession;
            this.timerInit();
        });
    }

    ngOnInit(): void {
        this.sectionContent = this.route.snapshot.data.video;
        this.video = this.route.snapshot.data.video.video;
        this.sectionContentSession = this.route.snapshot.data.sectionContentSession;
        this.companyId = this.storageService.get('company')?.id;

        const savedPosition = this.sectionContentSession?.playbackPosition ?? 0;
        if (savedPosition > RESUME_THRESHOLD_SECONDS && !this.sectionContentSession?.doneAt) {
            this.showResumePrompt = true;
        } else {
            this.initPlayer(0);
        }
    }

    ngOnDestroy(): void {
        this.videoSubscription.unsubscribe();
        // Capture current position before the player is destroyed
        if (this.player && this.sectionContentSession && !this.sectionContentSession.doneAt) {
            this.player.getPosition().then(pos => {
                const position = Math.floor(pos);
                if (position > 0) {
                    this.sectionContentSession.playbackPosition = position;
                    this.sectionContentSession.update(false, this.companyId).subscribe();
                }
            });
        }
        this.destroyPlayer();
    }

    resumeFromSaved(): void {
        this.showResumePrompt = false;
        this.initPlayer(this.sectionContentSession?.playbackPosition ?? 0);
    }

    startFromBeginning(): void {
        this.showResumePrompt = false;
        this.initPlayer(0);
    }

    private initPlayer(startPosition: number): void {
        if (!this.video?.url) return;

        setTimeout(async () => {
            const container = this.playerContainerRef?.nativeElement;
            if (!container) return;

            this.player = createVideoPlayer(this.video.url);

            this.player.onPlay(() => this.timerService.resume());
            this.player.onPause(() => {
                this.timerService.pause();
                this.savePosition();
            });
            this.player.onEnded(() => this.activityFinished());

            await this.player.load(container, this.video.url, startPosition);
            this.startSaveInterval();
        });
    }

    private startSaveInterval(): void {
        this.saveIntervalId = setInterval(() => this.savePosition(), SAVE_INTERVAL_MS);
    }

    private async savePosition(): Promise<void> {
        if (!this.player || !this.sectionContentSession) return;
        const position = Math.floor(await this.player.getPosition());
        if (position > 0) {
            this.sectionContentSession.playbackPosition = position;
            this.sectionContentSession.update(false, this.companyId).subscribe();
        }
    }

    private destroyPlayer(): void {
        clearInterval(this.saveIntervalId);
        this.player?.destroy();
        this.player = undefined;
    }

    activityFinished(): void {
        this.destroyPlayer();
        this.sectionContentSession.playbackPosition = 0;
        this.sectionContentSession.update(true, this.companyId).subscribe();
    }

    timerInit(): void {
        this.timerService.setTimer(this.sectionContentSession, 'content');
    }

    /** Alterna el modo pantalla completa por CSS (overlay a todo el viewport). */
    toggleMaximize(): void {
        this.isMaximized = !this.isMaximized;
    }

    /** Permite salir del modo maximizado con la tecla Escape. */
    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.isMaximized) {
            this.isMaximized = false;
        }
    }
}
